import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    input,
    untracked,
    viewChild,
} from "@angular/core";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25ChatComponent } from "../../../standalone/s25.chat.component";
import { DraggableDirective } from "../../s25-movable/s25.movable.element.directive";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { SocChatExternalType } from "../../../services/standout.classroom.service";
import { S25Browser } from "../../../util/core/s25.browser";
import { WorkflowChatController } from "./workflow.chat.controller";

/** The floating workflow chat panel shared by the My Workflow card chat and the workflow diagram hosts. */
@Component({
    selector: "workflow-chat-panel",
    template: `
        @let chat = controller();
        @if (chat.isOpen()) {
            <div
                draggableElement
                class="chat-panel"
                role="dialog"
                aria-modal="false"
                [attr.aria-labelledby]="headingId"
                [class.minimized]="chat.minimized()"
                (keydown.escape)="onEscape($event)"
            >
                <div class="chat-header" data-drag-handle>
                    <div class="header-left">
                        <s25-ng-icon [type]="'chat'" [ariaLabel]="false" />
                        <h3 #heading tabindex="-1" [id]="headingId">{{ chat.title() }}</h3>
                    </div>
                    <div class="header-controls">
                        @if (chat.loading()) {
                            <span class="chat-loading-spinner" role="status">Loading...</span>
                        }
                        <s25-ng-icon-button
                            class="minimize-btn"
                            [type]="chat.minimized() ? 'expand' : 'collapse'"
                            [ariaLabel]="chat.minimized() ? 'Expand chat' : 'Minimize chat'"
                            [size]="30"
                            (clicked)="$event.stopPropagation(); chat.minimized.set(!chat.minimized())"
                        ></s25-ng-icon-button>
                        <s25-ng-icon-button
                            [type]="'close'"
                            [ariaLabel]="'Close chat'"
                            [size]="20"
                            (clicked)="$event.stopPropagation(); chat.close()"
                        ></s25-ng-icon-button>
                    </div>
                </div>
                <div class="chat-content" [class.hidden]="chat.minimized()">
                    @if (!chat.loading()) {
                        <s25-chat
                            [channelId]="chat.channelKey()"
                            [channelTitle]="chat.channelTitle()"
                            [channelType]="chat.channelType()"
                            [socChannelId]="chat.socChannelId()"
                            [externalType]="SocChatExternalType.Workflow"
                        ></s25-chat>
                    }
                </div>
            </div>
        }
    `,
    host: {
        "[class.mobile]": "mobile",
    },
    styles: `
        .chat-panel {
            display: flex;
            flex-direction: column;
            width: min(600px, 100vw);
            height: min(600px, 100vh);
            background: var(--color--surface-2-bg);
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
        }

        .chat-panel.minimized {
            height: auto !important;
            resize: none;
        }

        .chat-header {
            cursor: move;
            padding: 12px 16px;
            background: var(--color--surface-bg);
            border-bottom: 2px solid var(--color--border);
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
        }

        .header-left {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 0;
        }

        .header-left h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: var(--color--text-strong);
            overflow-wrap: anywhere;
        }

        .header-controls {
            display: flex;
            gap: 4px;
            align-items: flex-start;
            flex-shrink: 0;
        }

        .chat-loading-spinner {
            font-size: 12px;
            color: var(--color--text-muted);
            margin-right: 8px;
        }

        .chat-content {
            flex: 1;
            height: 0;
            background: var(--color--surface-bg);
            overflow: hidden;
        }

        .chat-content.hidden {
            display: none;
        }

        :host.mobile {
            .chat-panel {
                position: fixed;
                inset: 0;
                width: auto;
                height: auto;
                border-radius: 0;
                resize: none;
                padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
                    env(safe-area-inset-left);
                background-color: var(--color--surface-bg);
            }

            .chat-panel.minimized {
                height: auto;
                inset: auto 0 0;
            }

            .chat-header {
                cursor: default;
            }

            .minimize-btn {
                display: none;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, S25ChatComponent, DraggableDirective, S25IconButtonComponent],
})
export class WorkflowChatPanelComponent {
    controller = input.required<WorkflowChatController>();

    protected readonly SocChatExternalType = SocChatExternalType;
    protected readonly mobile = S25Browser.mobileCheck();
    protected readonly headingId = `workflow-chat-heading-${Math.random().toString(36).slice(2, 8)}`;
    private readonly heading = viewChild<ElementRef<HTMLHeadingElement>>("heading");

    constructor() {
        afterRenderEffect(() => {
            const heading = this.heading()?.nativeElement;
            if (!heading) return;
            untracked(() => heading.focus());
        });
    }

    protected onEscape(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.controller().close();
    }
}
